import { readSignupLoginPhone, clearSignupLoginPhone } from "../shared/signup-duplicate";
import { useEffect, useRef, useState, useSyncExternalStore } from "react";
import { zodResolver } from "@hookform/resolvers/zod";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm } from "react-hook-form";
import { z } from "zod";
import { ApiError } from "@carepass/api-client";
import type { MemberRouteMetadata } from "../../app/routes";
import { memberRouter, memberSessionStore } from "../../app/runtime";
import { authApi, memberApi } from "../shared/api";
import type { MemberToken, TransactionGroup } from "../shared/types";
import { Alert, Button, ErrorState, FormActions, LoadingState, PageHeader, Status, Surface, navigate } from "../shared/ui";
import { SignupPinKeypad } from "../shared/SignupPinKeypad";
import { MemberTopBar } from "../member/MemberMobileUi";
import { TerminationResultPage } from "../member/TerminationResultPage";
import { clearTerminationResultSession } from "../member/termination-result-session";
import { IdentityVerificationForm } from "../shared/IdentityVerificationForm";
import { Dialog } from "@carepass/ui";
import { useIdentityExitGuard } from "./useIdentityExitGuard";
import { PasswordResetSteps } from "./PasswordResetSteps";
import { PasswordChangedNotice } from "./PasswordChangedNotice";
import "../signup/DirectSignupPage.css";
import { Brand, MemberFrame } from "../../design-review/MemberReferencePreview";
import { transactionPath } from "../member/transaction-groups";
import { cacheTransactionResult } from "../member/transaction-cache";
import { clearTransactionPaymentReturn, consumeTransactionPaymentReturn } from "../member/transaction-payment-return-context";

const subscribeSession = (listener: () => void) => memberSessionStore.subscribe(() => listener());
const getSession = () => memberSessionStore.get();

function safeReturnTo(): string {
  const value = new URLSearchParams(memberRouter.getSnapshot().location.search).get("returnTo");
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/app";
}

function saveSession(token: MemberToken) {
  clearTerminationResultSession();
  memberSessionStore.set({
    scope: "member",
    accessToken: token.accessToken,
    accessTokenExpiresAt: token.accessTokenExpiresAt,
    refreshToken: token.refreshToken,
    refreshTokenExpiresAt: token.refreshTokenExpiresAt,
  });
}

function feedback(error: unknown): string {
  if (error instanceof ApiError) {
    if (error.code === "MEMBER_CREDENTIALS_INVALID") return "휴대폰 번호 또는 비밀번호가 올바르지 않습니다.";
    if (error.code === "MEMBER_LOCKED") return "로그인 시도가 제한되었습니다. 본인확인 후 비밀번호를 재설정해 주세요.";
    if (error.code === "MEMBER_ACTIVATION_REQUIRED") return "최초 로그인에서 본인인증 후 비밀번호를 설정해 주세요.";
    if (error.code === "MEMBER_IDENTITY_REQUEST_INVALID") return "입력한 정보로 진행 가능한 회원을 찾지 못했습니다. 처음 이용한다면 제휴 가입과 최초 결제를 먼저 완료해 주세요.";
    return error.message;
  }
  return "요청을 처리하지 못했습니다. 잠시 후 다시 시도해 주세요.";
}

export function requiresFirstLoginActivation(error: unknown): boolean {
  return error instanceof ApiError && error.code === "MEMBER_ACTIVATION_REQUIRED";
}

const LOGIN_PHONE_KEY = "carepass.login.phone";

function formatPhone(value: string): string {
  return value.replace(/\D/g, "").slice(0, 11).replace(/^(\d{3})(\d{0,4})(\d{0,4}).*/, (_, a, b, c) => [a, b, c].filter(Boolean).join("-"));
}

function loginReturnTo(): string {
  const value = new URLSearchParams(memberRouter.getSnapshot().location.search).get("returnTo");
  return value?.startsWith("/") && !value.startsWith("//") ? value : "/app";
}

function authHeader(title?: string) {
  return <header className="member-auth-header"><button type="button" onClick={() => navigate("/")} aria-label="CarePass 홈"><Brand /></button>{title ? <strong>{title}</strong> : <span />}<button type="button" className="member-login-close" onClick={() => navigate("/")}>닫기 ×</button></header>;
}

function LoginPage() {
  const [phone, setPhone] = useState(readSignupLoginPhone);
  useEffect(clearSignupLoginPhone, []);
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const submit = async () => {
    if (phone.length !== 11) return;
    setError(undefined);
    setPending(true);
    try {
      const decision = await authApi.entryDecision({ phoneNumber: phone });
      sessionStorage.setItem(LOGIN_PHONE_KEY, phone);
      if (decision.nextAction === "PASSWORD") {
        navigate(`/login/password?returnTo=${encodeURIComponent(loginReturnTo())}`);
      } else if (decision.nextAction === "IDENTITY_VERIFICATION") {
        navigate(`/activate?phoneNumber=${encodeURIComponent(phone)}`);
      } else {
        navigate(`/signup?phoneNumber=${encodeURIComponent(phone)}`);
      }
    } catch (cause) {
      setError(feedback(cause));
    } finally {
      setPending(false);
    }
  };
  return <MemberFrame onNavigate={() => undefined}><div className="mr-auth">{authHeader()}<section className="mr-auth-card member-login-step member-login-step--phone"><div className="mr-auth-card__intro"><h1>휴대폰 번호를<br/>입력해 주세요</h1><p>가입 여부를 확인한 뒤 다음 단계로 안내할게요.</p></div><label className="mr-field"><span>휴대폰 번호</span><input autoFocus value={formatPhone(phone)} placeholder="010-0000-0000" inputMode="tel" autoComplete="tel" onChange={(event) => setPhone(event.currentTarget.value.replace(/\D/g, "").slice(0, 11))}/></label>{error && <p className="mr-auth-error" role="alert">{error}</p>}<button className="mr-primary" type="button" disabled={pending || phone.length !== 11} onClick={() => void submit()}>{pending ? "확인 중…" : "다음"}</button><p className="mr-auth__help">처음 이용하시나요? <button type="button" onClick={() => navigate("/signup")}>회원가입</button></p></section></div></MemberFrame>;
}

function LoginPasswordPage() {
  const phone = sessionStorage.getItem(LOGIN_PHONE_KEY) ?? "";
  const [pin, setPin] = useState("");
  const [error, setError] = useState<string>();
  const [pending, setPending] = useState(false);
  const append = (value: string) => setPin((current) => current.length < 6 ? `${current}${value}` : current);
  const submit = async () => {
    if (phone.length !== 11 || pin.length !== 6) return;
    setError(undefined); setPending(true);
    try {
      const token = await authApi.login({ phoneNumber: phone, password: pin, deviceId: window.navigator.userAgent.slice(0, 255) });
      saveSession(token);
      sessionStorage.removeItem(LOGIN_PHONE_KEY);
      navigate(safeReturnTo(), true);
    } catch (cause) {
      if (requiresFirstLoginActivation(cause)) { navigate(`/activate?phoneNumber=${encodeURIComponent(phone)}`, true); return; }
      setError(feedback(cause));
    } finally { setPending(false); }
  };
  if (phone.length !== 11) { navigate("/login", true); return null; }
  return <MemberFrame onNavigate={() => undefined}><div className="mr-auth">{authHeader("로그인")}<section className="mr-auth-card member-login-step"><div className="mr-auth-card__intro"><h1>비밀번호를<br/>입력해 주세요</h1><p>{formatPhone(phone)}</p></div><p className="member-pin-label">비밀번호 · 숫자 6자리</p><div className="mr-pin" aria-label={`비밀번호 ${pin.length}자리 입력됨`}>{Array.from({length:6},(_,index)=><span key={index} data-filled={index < pin.length}/>)}</div><div className="mr-keypad" aria-label="숫자 키패드">{["1","2","3","4","5","6","7","8","9","","0","delete"].map((key,index)=>key===""?<span key={`empty-${index}`}/>:<button key={key} type="button" aria-label={key==="delete"?"한 자리 지우기":key} onClick={()=>key==="delete"?setPin(current=>current.slice(0,-1)):append(key)}>{key==="delete"?"⌫":key}</button>)}</div>{error&&<p className="mr-auth-error" role="alert">{error}</p>}<button className="mr-primary" type="button" disabled={pending||pin.length!==6} onClick={()=>void submit()}>{pending?"로그인 중…":"로그인"}</button><div className="mr-auth-links"><button type="button" onClick={()=>{sessionStorage.removeItem(LOGIN_PHONE_KEY);navigate("/login");}}>휴대폰 번호 변경</button><span aria-hidden="true"/><button type="button" onClick={()=>navigate(`/password-reset?phoneNumber=${encodeURIComponent(phone)}`)}>비밀번호 찾기</button></div></section></div></MemberFrame>;
}

const pinSchema = z.object({
  password: z.string().regex(/^\d{6}$/, "숫자 6자리 비밀번호를 입력해 주세요."),
  passwordConfirmation: z.string(),
}).refine((value) => value.password === value.passwordConfirmation, {
  path: ["passwordConfirmation"],
  message: "비밀번호가 서로 다릅니다.",
});

type PinValues = z.infer<typeof pinSchema>;

function IdentityFlow({ purpose, activation }: { readonly purpose: "ACTIVATION" | "PASSWORD_RESET"; readonly activation: boolean }) {
  const [verificationToken, setVerificationToken] = useState<string>();
  const [confirmed, setConfirmed] = useState(false);
  const [activePin, setActivePin] = useState<"pin" | "confirm">("pin");
  const [error, setError] = useState<string>();
  const phoneNumber = new URLSearchParams(memberRouter.getSnapshot().location.search).get("phoneNumber") ?? "";
  const [awaitingCode, setAwaitingCode] = useState(false);
  const [resetStep, setResetStep] = useState<"new" | "confirmation">("new");
  const [resetCompleted, setResetCompleted] = useState(false);
  const [resetPending, setResetPending] = useState(false);
  const [identityVisit, setIdentityVisit] = useState(0);
  const pin = useForm<PinValues>({ resolver: zodResolver(pinSchema) });
  const identityExit = useIdentityExitGuard(`carepass.identity.${purpose}`, () => {
    setVerificationToken(undefined); setConfirmed(false); setAwaitingCode(false); setResetStep("new"); setResetPending(false); setIdentityVisit(value => value + 1); pin.reset();
  });

  const complete = pin.handleSubmit(async (values) => {
    if (!verificationToken) return;
    setError(undefined);
    try {
      if (activation) {
        const token = await authApi.activate(verificationToken, {
          ...values,
          deviceId: window.navigator.userAgent.slice(0, 255),
        });
        saveSession(token);
        identityExit.finish();
        navigate("/app", true);
      }
    } catch (cause) {
      setError(feedback(cause));
    }
  });

  if (resetCompleted) return <PasswordChangedNotice />;
  const progress = activation ? confirmed ? 3 : awaitingCode ? 2 : 1 : confirmed ? resetStep === "confirmation" ? 3 : 2 : 1;
  return (
    <div className={`auth-page member-identity-card${activation ? " member-identity-card--activation" : ""}`}><div className="member-signup__progress" aria-label={`3단계 중 ${progress}단계`}>{[1,2,3].map(step=><span key={step} data-active={step <= progress}/>)}</div>
      <PageHeader
        eyebrow={confirmed ? activation ? "STEP 3 · 최초 비밀번호 설정" : resetStep === "new" ? "STEP 2 · 새 비밀번호 설정" : "STEP 3 · 새 비밀번호 재확인" : awaitingCode ? `${activation ? "STEP 2" : "STEP 1"} · 인증번호 확인` : "STEP 1 · 본인 확인"}
        title={confirmed ? !activation && resetStep === "confirmation" ? "새 비밀번호를 한 번 더 입력해 주세요" : "로그인 비밀번호를 설정해 주세요" : awaitingCode ? "문자로 받은 인증번호를 입력해 주세요" : "가입하신 휴대폰으로 본인 확인할게요"}
        description={activation ? "최초 로그인은 본인인증과 비밀번호 설정 후 완료됩니다." : "비밀번호 재설정을 위해 본인 확인이 필요합니다."}
      />
      {activation && !confirmed && !awaitingCode && (
        <Alert
          persistent
          variant="information"
          title="비밀번호 설정 후 로그인할 수 있습니다"
          action={<Button variant="quiet" onClick={() => navigate("/login")}>휴대폰 번호 변경</Button>}
        >
          가입을 완료하고 비밀번호를 설정하지 않았다면 본인인증 후 설정을 이어갈 수 있습니다. 가입할 때 사용한 본인정보를 입력해 주세요. 결제는 다시 진행하지 않습니다.
        </Alert>
      )}
      {error && <Alert persistent variant="danger" title="진행하지 못했습니다">{error}</Alert>}
      {identityExit.initializing ? (
        identityExit.error ? <Alert persistent variant="danger" title="본인인증을 다시 시작하지 못했습니다" action={<Button onClick={() => void identityExit.restart()}>다시 시도</Button>}>{identityExit.error}</Alert>
          : <LoadingState label="이전 본인인증을 종료하고 있습니다." />
      ) : !confirmed ? (
        <Surface title={awaitingCode ? "2. 인증번호 확인" : "1. 본인정보 입력"}>
          <IdentityVerificationForm
            key={`identity-visit-${identityVisit}`}
            storageKey={`carepass.identity.${purpose}`}
            initialPhoneNumber={phoneNumber}
            phoneNumberLocked={activation && /^01\d{8,9}$/.test(phoneNumber)}
            restoreStoredAttempt={false}
            onDirtyChange={identityExit.markDirty}
            client={{ request: async input => { identityExit.track(input.requestKey); const result = await authApi.requestIdentity({ ...input, purpose }); if (!identityExit.track(result.verificationToken, input.requestKey)) throw new ApiError({ kind: "aborted", message: "본인인증이 중단되었습니다. 다시 시작해 주세요." }); return result; }, status: authApi.identityStatus, confirm: authApi.confirmIdentity, resend: authApi.resendIdentity, complete: authApi.completeIdentity }}
            onStageChange={stage => setAwaitingCode(stage === "code")}
            onVerified={token => { setVerificationToken(token); setConfirmed(true); }}
          />
        </Surface>
      ) : !activation && verificationToken ? (
        <PasswordResetSteps verificationToken={verificationToken} onStepChange={setResetStep} onPendingChange={setResetPending} onFinishing={identityExit.finish}
          onCompleted={() => { setVerificationToken(undefined); setResetCompleted(true); }}
          onRestart={async reason => { await identityExit.abandonCurrent(); setVerificationToken(undefined); setConfirmed(false); setResetStep("new"); setAwaitingCode(false); setError(reason ?? "본인확인 시간이 지났거나 변경이 종료되었습니다. 본인인증부터 다시 진행해 주세요."); }} />
      ) : (
        <Surface key="password" title="3. 새 비밀번호 등록" description="다른 서비스와 겹치지 않는 숫자 6자리를 사용해 주세요.">
          <form className="stack" onSubmit={(event) => void complete(event)} noValidate>
            <SignupPinKeypad value={pin.watch("password") ?? ""} confirmation={pin.watch("passwordConfirmation") ?? ""} active={activePin} onActive={setActivePin} onChange={(target, value) => pin.setValue(target === "pin" ? "password" : "passwordConfirmation", value, { shouldValidate: true })}/>
            {pin.formState.errors.passwordConfirmation && <p role="alert">{pin.formState.errors.passwordConfirmation.message}</p>}
            <FormActions pending={pin.formState.isSubmitting} submitLabel={activation ? "활성화 완료" : "비밀번호 변경"} />
          </form>
        </Surface>
      )}
      <Dialog open={Boolean(identityExit.destination)} title="본인인증을 중단할까요?"
        description="진행 중인 본인인증과 입력 정보가 초기화됩니다. 나가시겠습니까? 다시 들어오면 본인인증부터 진행해야 합니다."
        closeDisabled={identityExit.pending || resetPending} onClose={identityExit.stay}
        secondaryAction={<Button variant="secondary" disabled={identityExit.pending || resetPending} onClick={identityExit.stay}>계속하기</Button>}
        primaryAction={<Button variant="danger" disabled={resetPending} isLoading={identityExit.pending} onClick={() => void identityExit.leave()}>나가기</Button>}
      >{resetPending && <p>비밀번호 변경 결과를 확인하고 있습니다. 잠시 기다려 주세요.</p>}{identityExit.error && !identityExit.initializing && <Alert persistent variant="danger" title="화면을 나가지 못했습니다">{identityExit.error}</Alert>}</Dialog>
    </div>
  );
}

function PaymentResultRecoveryPage() {
  const client = useQueryClient();
  const session = useSyncExternalStore(subscribeSession, getSession, () => null);
  const snapshot = memberRouter.getSnapshot();
  const current = `${snapshot.location.pathname}${snapshot.location.search}`;
  const rawTransactionId = new URLSearchParams(snapshot.location.search).get("transactionId");
  const parsedTransactionId = rawTransactionId && /^\d+$/.test(rawTransactionId) ? Number(rawTransactionId) : undefined;
  const transactionId = parsedTransactionId && Number.isSafeInteger(parsedTransactionId) ? parsedTransactionId : undefined;
  const [returnGroup, setReturnGroup] = useState<TransactionGroup>("ALL");
  const restoredContext = useRef<{ transactionId: number; memberId: number; group: TransactionGroup } | undefined>(undefined);
  const transaction = useQuery({
    queryKey: ["member", "payment-return", transactionId],
    queryFn: () => memberApi.transaction(transactionId!),
    enabled: Boolean(session && transactionId),
    retry: false,
  });

  useEffect(() => {
    const result = transaction.data;
    if (session && !transactionId) clearTransactionPaymentReturn();
    if (!session || !transactionId || !result) return;
    if (result.id !== transactionId) { clearTransactionPaymentReturn(); setReturnGroup("ALL"); return; }
    const restored = restoredContext.current;
    const group = restored?.transactionId === result.id && restored.memberId === result.memberId ? restored.group : consumeTransactionPaymentReturn(result);
    restoredContext.current = { transactionId: result.id, memberId: result.memberId, group };
    setReturnGroup(group);
    cacheTransactionResult(client, result);
    if (result.paymentStatus === "APPROVED"
      || ["PAYMENT_PROCESSING", "PAYMENT_UNKNOWN", "TRANSFER_SELECTION_PENDING", "TRANSFER_READY", "TRANSFER_PROCESSING", "TRANSFER_UNKNOWN", "TRANSFER_FAILED", "COMPLETED"].includes(result.status)) {
      navigate(transactionPath(`/app/transactions/${transactionId}/payment`, group), true);
    }
  }, [client, session, transactionId, transaction.data]);

  let content;
  if (!transactionId) {
    content = (
      <Alert persistent variant="danger" title="확인할 거래번호가 없습니다">
        결제를 진행한 거래 상세에서 최신 상태를 확인해 주세요.
      </Alert>
    );
  } else if (!session) {
    content = (
      <Alert persistent variant="information" title="로그인이 필요합니다">
        로그인하면 결제와 송금 진행 상태를 확인할 수 있습니다.
      </Alert>
    );
  } else if (transaction.isPending) {
    content = <LoadingState label="결제 결과를 확인하고 있습니다." />;
  } else if (transaction.isError) {
    content = <ErrorState error={transaction.error} onRetry={() => void transaction.refetch()} />;
  } else {
    const result = transaction.data;
    const resultVariant = result.paymentStatus === "APPROVED"
      ? "success"
      : result.paymentStatus === "UNKNOWN" || result.status === "PAYMENT_UNKNOWN"
        ? "information"
        : "danger";
    content = (
      <>
        <Alert persistent variant={resultVariant} title={result.paymentStatus === "APPROVED" ? "결제가 승인되었습니다" : result.paymentStatus === "UNKNOWN" ? "결제 결과를 확인하고 있습니다" : "결제가 완료되지 않았습니다"}>
          결제와 송금은 별도로 처리됩니다. 아래에서 현재 진행 상태를 확인해 주세요.
        </Alert>
        <dl className="detail-list">
          <div><dt>거래번호</dt><dd>{result.transactionNo}</dd></div>
          <div><dt>거래 상태</dt><dd><Status>{result.status}</Status></dd></div>
          <div><dt>결제 상태</dt><dd>{result.paymentStatus ? <Status>{result.paymentStatus}</Status> : "결제 전"}</dd></div>
          <div><dt>송금 상태</dt><dd>{result.transferStatus ? <Status>{result.transferStatus}</Status> : "송금 전"}</dd></div>
        </dl>
      </>
    );
  }

  return (
    <div className="auth-page">
      <PageHeader
        eyebrow="결제 결과"
        title="결제·송금 진행 상태"
        description="처리 결과를 확인해 주세요."
      />
      <Surface>
        {content}
        <div className="form-actions">
          {!session && <Button size="large" onClick={() => navigate(`/login?returnTo=${encodeURIComponent(current)}`)}>로그인하고 결과 확인</Button>}
          {session && transactionId && <Button size="large" onClick={() => navigate(transactionPath(`/app/transactions/${transactionId}`, returnGroup))}>거래 상세 보기</Button>}
          <Button variant="secondary" onClick={() => navigate(transactionPath("/app/transactions", returnGroup))}>거래 목록</Button>
        </div>
      </Surface>
    </div>
  );
}


export function AuthPage({ route }: { readonly route: MemberRouteMetadata }) {
  if (route.id === "AUTH-S01") return new URLSearchParams(memberRouter.getSnapshot().location.search).get("passwordChanged") === "1" ? <PasswordChangedNotice /> : <LoginPage />;
  if (route.id === "AUTH-S05") return <LoginPasswordPage />;
  if (route.id === "AUTH-S02") return <><MemberTopBar title="최초 로그인" backTo="/login"/><IdentityFlow key="ACTIVATION" purpose="ACTIVATION" activation /></>;
  if (route.id === "AUTH-S03") return <><MemberTopBar title="비밀번호 재설정" backTo="/login"/><IdentityFlow key="PASSWORD_RESET" purpose="PASSWORD_RESET" activation={false} /></>;
  if (route.id === "PAY-S01") return <PaymentResultRecoveryPage />;
  return <TerminationResultPage />;
}
